import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { sendFeedback } from "@/services/multi-agent";
import { FEEDBACK_RATING } from "@/constants/multi-agent/feedback.constants";

export interface UseBadResponseFeedbackReturn {
  open: boolean;
  selectedReasons: Set<string>;
  comment: string;
  submitting: boolean;
  submitted: boolean;
  error: string | null;
  openDialog: () => void;
  closeDialog: () => void;
  toggleReason: (id: string) => void;
  setComment: (text: string) => void;
  submit: () => Promise<void>;
  clearError: () => void;
}

/**
 * Manages the full lifecycle of a bad-response feedback interaction for one
 * Weave message. Pass the message's runId — the hook holds open/closed state,
 * selected reasons, the optional comment, and the async submit flow.
 *
 * Extend points:
 *  - Wire FEEDBACK_EVENTS.* into your analytics provider in openDialog/submit.
 *  - Add conversationId / modelId context by passing extra metadata here.
 */
export function useBadResponseFeedback(runId: string | null): UseBadResponseFeedbackReturn {
  const [open, setOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(new Set());
  const [comment, setCommentState] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openDialog = useCallback(() => {
    if (submitted) return;
    setOpen(true);
    // analytics: track(FEEDBACK_EVENTS.OPENED, { runId })
  }, [submitted]);

  const closeDialog = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    setError(null);
    if (!submitted && runId) {
      // Record the negative signal even when the user closes without filling details.
      // setSubmitted first (optimistic) so the UI can't re-open while the request is in-flight.
      // Feedback is non-critical telemetry — a silent failure is acceptable over blocking UX.
      setSubmitted(true);
      void sendFeedback(runId, FEEDBACK_RATING.NEGATIVE, null, []).catch((e) => {
        console.error("[Weave feedback] silent dismiss failed:", e);
      });
    }
  }, [submitting, submitted, runId]);

  const toggleReason = useCallback((id: string) => {
    setSelectedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setComment = useCallback((text: string) => {
    setCommentState(text);
  }, []);

  const submit = useCallback(async () => {
    if (!runId || submitting) return;
    setSubmitting(true);
    setError(null);
    const reasons = Array.from(selectedReasons);
    const trimmedComment = comment.trim();
    try {
      await sendFeedback(runId, FEEDBACK_RATING.NEGATIVE, trimmedComment || null, reasons);
      setSubmitted(true);
      setOpen(false);
      toast.success("Thanks for your feedback. It helps improve Weave.", { duration: 4000 });
      // analytics: track(FEEDBACK_EVENTS.SUBMITTED, {
      //   runId, reasons, commentLength: trimmedComment.length
      // })
    } catch (e) {
      setError("Couldn't send feedback — please try again.");
      console.error("[Weave feedback]", e);
    } finally {
      setSubmitting(false);
    }
  }, [runId, submitting, comment, selectedReasons]);

  const clearError = useCallback(() => setError(null), []);

  return {
    open,
    selectedReasons,
    comment,
    submitting,
    submitted,
    error,
    openDialog,
    closeDialog,
    toggleReason,
    setComment,
    submit,
    clearError,
  };
}
